import queryString from 'query-string';
import { apiUrl, authHeaders } from './auth';

export const fetchUsers = async ({
    page = 1,
    role_id = null,
    department_id = null,
    status = '',
    name = '',
    staff_only = '',
    super_admin = '',
    admin_site = '',
}) => {
    const filterParams = queryString.stringify(
        {
            page,
            role_id,
            department_id,
            status,
            name,
            staff_only,
            super_admin,
            admin_site,
        },
        {
            skipNull: true,
            skipEmptyString: true,
            strict: false,
        }
    );
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users?${filterParams}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching users');
    }
    const json = await resp.json();
    return json;
};

export const updateUser = async (user_id: string | number, data: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}`;
    const resp = await fetch(url, {
        method: 'put',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        const errorText = await resp.text();
        try {
            const parsed = JSON.parse(errorText);
            throw new Error(parsed?.error || parsed?.detail || 'Error updating user');
        } catch (err) {
            // If not JSON, use the raw error text
            throw new Error(errorText || 'Error updating user');
        }
    }
    const json = await resp.json();
    return json;
};

export const createUser = async (data: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/create`;
    const resp = await fetch(url, {
        method: 'post',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        const errorText = await resp.text();
        try {
            const parsed = JSON.parse(errorText);
            throw new Error(parsed?.error || parsed?.detail || 'Error creating user');
        } catch (err) {
            // If not JSON, use the raw error text
            throw new Error(errorText || 'Error creating user');
        }
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignsOfUser = async ({ user_id = '' }) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/campaigns`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching campaigns');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignRequestsOfUser = async ({ user_id = '' }) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/campaign/requests`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching campaigns');
    }
    const json = await resp.json();
    return json;
};

export const fetchInitialCampaignRequestsOfUser = async ({ user_id = '' }) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/initial/campaign/requests`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching campaigns');
    }
    const json = await resp.json();
    return json;
};

export const fetchRoles = async () => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/groups`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching roles');
    }
    const json = await resp.json();
    return json;
};

export const fetchDepartments = async (): Promise<
    { id: number; name: string }[]
> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/departments`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching departments');
    }
    const json = await resp.json();
    return json;
};

export const fetchUser = async (id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${id}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching user');
    }
    const json = await resp.json();
    return json;
};

export const deactivateUser = async (user_id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/deactivate`;
    const resp = await fetch(url, {
        method: 'put',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error deactivating user');
    }
    const json = await resp.json();
    return json;
};

export const activateUser = async (user_id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/activate`;
    const resp = await fetch(url, {
        method: 'put',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error activating user');
    }
    const json = await resp.json();
    return json;
};

export const resetUserPassword = async (user_id: string | number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/reset-password`;
    const resp = await fetch(url, {
        method: 'post',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error resetting user password');
    }
    const json = await resp.json();
    return json;
};

export const setUserPassword = async (user_id: string | number, password: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/users/${user_id}/set-password`;
    const resp = await fetch(url, {
        method: 'post',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
    });
    if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.message || 'Error setting password');
    }
    const json = await resp.json();
    return json;
};

export const fetchContributions = async ({
    page = 1,
    contributor = '',
    from = '',
    to = '',
    status = '',
}) => {
    const headers = authHeaders();

    const params = new URLSearchParams({
        page: page.toString(),
        contributor,
        from,
        to,
        status,
    });

    const url = `${apiUrl}/admin/contributions?${params.toString()}`;

    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Error fetching contributions');
    }
    const json = await resp.json();
    return json;
};
